import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Row, Col, Card, CardBody, CardHeader, CardTitle,
  Button, FormGroup, Input, Spinner, Label,
} from 'reactstrap';
import { toast } from 'react-hot-toast';
import { fetchItemsBySoNumber, submitComplaintDo } from './actions/complaintsDoActions';
import { resetComplaintsDo } from '../../Redux/complaintsDo/complaintsDoSlice';

const ComplaintsDoForm = ({ onClose, onSuccess }) => {
  const dispatch = useDispatch();

  const { soItems, soOrderInfo, isLoading, submitSuccess, submitError } = useSelector(
    (state) => state.complaintsDo
  );

  const [soNumber,       setSoNumber]       = useState('');
  const [searched,       setSearched]       = useState(false);
  const [selectedItems,  setSelectedItems]  = useState([]);
  const [driverName,     setDriverName]     = useState('');
  const [complaintNo,    setComplaintNo]    = useState('');
  const [isSearching,    setIsSearching]    = useState(false);
  const [isSubmitting,   setIsSubmitting]   = useState(false);

  useEffect(() => {
    if (submitSuccess) {
      toast.success('DO submitted successfully!', {
        style: { fontSize: 16, fontWeight: 600, padding: '14px 20px' },
        duration: 4000,
      });
      setSoNumber('');
      setSearched(false);
      setSelectedItems([]);
      setDriverName('');
      setComplaintNo('');
      dispatch(resetComplaintsDo());
      if (onSuccess) onSuccess(submitSuccess);
    }
  }, [submitSuccess]);

  useEffect(() => {
    if (submitError) toast.error(submitError);
  }, [submitError]);

  useEffect(() => {
    return () => dispatch(resetComplaintsDo());
  }, []);

  // ── Search ────────────────────────────────────────────────────────────────
  const handleSearch = async () => {
    const trimmed = soNumber.trim();
    if (!trimmed) return;
    setSelectedItems([]);
    setSearched(false);
    setIsSearching(true);
    try {
      await fetchItemsBySoNumber({ soNumber: trimmed, dispatch });
      setSearched(true);
    } finally {
      setIsSearching(false);
    }
  };

  // ── Item selection ────────────────────────────────────────────────────────
  const toggleItem = (item) => {
    if (item.is_added === 1) return;
    const exists = selectedItems.find((s) => s.sku === item.sku);
    if (exists) {
      setSelectedItems((prev) => prev.filter((s) => s.sku !== item.sku));
    } else {
      setSelectedItems((prev) => [
        ...prev,
        { sku: item.sku, item_name: item.name || '', qty: item.qty || 1, return_qty: 1, remarks: '', is_returnable: 0 },
      ]);
    }
  };

  const isSelected  = (sku) => selectedItems.some((s) => s.sku === sku);
  const getSelItem  = (sku) => selectedItems.find((s) => s.sku === sku);
  const updateField = (sku, field, value) =>
    setSelectedItems((prev) => prev.map((s) => (s.sku === sku ? { ...s, [field]: value } : s)));

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!soNumber.trim())           return toast.error('SO Number is required.');
    if (!complaintNo.trim())        return toast.error('Complaint No is required.');
    if (selectedItems.length === 0) return toast.error('Please select at least one item.');

    const authUser  = JSON.parse(localStorage.getItem('authUser') || '{}');
    const createdBy = authUser?.id || authUser?.user_id || authUser?.userId || '';

    setIsSubmitting(true);
    try {
      await submitComplaintDo({
        payload: {
          so_number:    soNumber.trim(),
          full_name:    soOrderInfo?.name    || '',
          address:      soOrderInfo?.address || '',
          phone_number: soOrderInfo?.phone   || '',
          complaint_no: complaintNo.trim(),
          driver_name:  driverName,
          status:       'pending',
          created_by:   createdBy,
          items: selectedItems.map(({ sku, item_name, qty, is_returnable, remarks }) => ({
            sku,
            name:          item_name,
            qty:           Number(qty),
            is_returnable: Number(is_returnable),
            remarks,
            status:        'pending',
          })),
        },
        dispatch,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    dispatch(resetComplaintsDo());
    if (onClose) onClose();
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      {/* ── Search Row ── */}
      <Row>
        <Col className="p-3 bg-soft-light border-1 border-light">
          <FormGroup className="position-relative w-100" style={{ maxWidth: 420, marginBottom: 0 }}>
            <Input
              type="text"
              className="form-control"
              placeholder="🔍 Enter SO Number (e.g. SO12345)"
              value={soNumber}
              onChange={(e) => setSoNumber(e.target.value)}
              autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSearch(); } }}
            />
            <Button
              color="primary"
              className="position-absolute top-0 end-0"
              onClick={handleSearch}
              disabled={isSearching || !soNumber.trim()}
            >
              {isSearching
                ? <><Spinner size="sm" className="me-1" /> Searching...</>
                : <><i className="bx bx-search-alt align-middle"></i> Search</>
              }
            </Button>
          </FormGroup>
        </Col>
      </Row>

      {/* ── Results Card ── */}
      {searched && (
        <Row className="mt-3">
          <Col md={12}>
            <Card className="shadow border-0">
              <CardHeader style={{ background: '#f5f6fa', borderBottom: '2px solid #e3e6ed', padding: '18px 24px' }}>
                <CardTitle tag="h5" className="mb-0 d-flex align-items-center">
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    background: 'linear-gradient(135deg, #1976d2 60%, #42a5f5 100%)',
                    color: '#fff', borderRadius: '50%', width: 34, height: 34,
                    marginRight: 12, flexShrink: 0,
                  }}>
                    <i className="bx bx-package" style={{ fontSize: 18 }}></i>
                  </span>
                  <span style={{ fontWeight: 700, color: '#222', fontSize: 16 }}>
                    Items for SO: <span style={{ color: '#1976d2' }}>{soNumber}</span>
                  </span>
                  {soItems.length > 0 && (
                    <span className="badge bg-soft-info text-dark ms-3" style={{ fontSize: 12 }}>
                      {soItems.length} items found
                    </span>
                  )}
                </CardTitle>
              </CardHeader>

              <CardBody style={{ padding: '0' }}>
                {/* ── Order Info ── */}
                {soOrderInfo && (soOrderInfo.name || soOrderInfo.address || soOrderInfo.phone) && (
                  <div style={{ display: 'flex', gap: 12, padding: '16px 20px', background: '#f8f9fa', borderBottom: '1px solid #e3e6ed', flexWrap: 'wrap' }}>
                    {soOrderInfo.name && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#fff', border: '1px solid #e3e6ed', borderRadius: 8, padding: '10px 16px', minWidth: 180 }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: '#e8f4fd', borderRadius: '50%', width: 36, height: 36, flexShrink: 0 }}>
                          <i className="bx bx-user" style={{ fontSize: 18, color: '#1976d2' }}></i>
                        </span>
                        <div>
                          <div style={{ fontSize: 11, color: '#adb5bd', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Customer</div>
                          <div style={{ fontSize: 14, color: '#343a40', fontWeight: 700, marginTop: 2 }}>{soOrderInfo.name}</div>
                        </div>
                      </div>
                    )}
                    {soOrderInfo.phone && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#fff', border: '1px solid #e3e6ed', borderRadius: 8, padding: '10px 16px', minWidth: 160 }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: '#e8f8f0', borderRadius: '50%', width: 36, height: 36, flexShrink: 0 }}>
                          <i className="bx bx-phone" style={{ fontSize: 18, color: '#2e7d32' }}></i>
                        </span>
                        <div>
                          <div style={{ fontSize: 11, color: '#adb5bd', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Mobile</div>
                          <div style={{ fontSize: 14, color: '#343a40', fontWeight: 700, marginTop: 2 }}>{soOrderInfo.phone}</div>
                        </div>
                      </div>
                    )}
                    {soOrderInfo.address && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#fff', border: '1px solid #e3e6ed', borderRadius: 8, padding: '10px 16px', flex: 1, minWidth: 220 }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: '#fff3e0', borderRadius: '50%', width: 36, height: 36, flexShrink: 0 }}>
                          <i className="bx bx-map" style={{ fontSize: 18, color: '#e65100' }}></i>
                        </span>
                        <div>
                          <div style={{ fontSize: 11, color: '#adb5bd', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Address</div>
                          <div style={{ fontSize: 14, color: '#343a40', fontWeight: 600, marginTop: 2 }}>{soOrderInfo.address}</div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {soItems.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="bx bx-search-alt" style={{ fontSize: 40, color: '#adb5bd' }}></i>
                    <div style={{ color: '#888', marginTop: 10, fontSize: 14 }}>
                      No items found for <strong>{soNumber}</strong>. Check the SO number and try again.
                    </div>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover table-nowrap mb-0">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: 46 }}></th>
                          <th>SKU</th>
                          <th style={{ width: '20%', minWidth: 120 }}>Item Name</th>
                          <th className="text-center" style={{ width: 80 }}>Qty</th>
                          <th className="text-center" style={{ width: 110 }}>Return Qty</th>
                          <th className="text-center" style={{ width: 120 }}>Returnable</th>
                          <th style={{ minWidth: 180 }}>Remarks</th>
                        </tr>
                      </thead>
                      <tbody>
                        {soItems.map((item, idx) => {
                          const sel     = isSelected(item.sku);
                          const selItem = getSelItem(item.sku);
                          const added   = item.is_added === 1;
                          return (
                            <tr
                              key={item.sku || idx}
                              style={{
                                background:  added ? '#f8f9fa' : sel ? '#e3f2fd' : '',
                                borderLeft:  sel ? '3px solid #1976d2' : '3px solid transparent',
                                cursor:      added ? 'not-allowed' : 'pointer',
                                opacity:     added ? 0.6 : 1,
                              }}
                              onClick={() => !added && toggleItem(item)}
                            >
                              <td onClick={(e) => e.stopPropagation()}>
                                <Input
                                  type="checkbox"
                                  checked={sel}
                                  disabled={added}
                                  onChange={() => !added && toggleItem(item)}
                                  style={{ width: 17, height: 17, cursor: added ? 'not-allowed' : 'pointer' }}
                                />
                              </td>
                              <td>
                                <span style={{
                                  background: '#e9ecef', color: '#343a40',
                                  padding: '2px 8px', borderRadius: 4,
                                  fontSize: 12, fontWeight: 700,
                                }}>
                                  {item.sku}
                                </span>
                              </td>
                              <td style={{ color: sel ? '#1976d2' : '#495057', fontWeight: sel ? 600 : 400, fontSize: 14, whiteSpace: 'normal', wordBreak: 'break-word', maxWidth: 180 }}>
                                {item.name
                                  ? item.name.length > 120
                                    ? `${item.name.substring(0, 120)}...`
                                    : item.name
                                  : '—'}
                              </td>
                              <td className="text-center">
                                <span className="badge bg-soft-dark text-dark">{item.qty || 1}</span>
                              </td>
                              <td className="text-center" onClick={(e) => e.stopPropagation()}>
                                <Input
                                  type="number"
                                  min={1}
                                  max={item.qty || 1}
                                  disabled={!sel || added}
                                  value={selItem?.return_qty ?? 1}
                                  onChange={(e) => updateField(item.sku, 'return_qty', e.target.value)}
                                  style={{ width: 75, textAlign: 'center', opacity: sel && !added ? 1 : 0.35, padding: '4px 8px', fontSize: 13 }}
                                />
                              </td>
                              <td className="text-center" onClick={(e) => e.stopPropagation()}>
                                <Input
                                  type="select"
                                  disabled={!sel || added}
                                  value={selItem?.is_returnable ?? 0}
                                  onChange={(e) => updateField(item.sku, 'is_returnable', Number(e.target.value))}
                                  style={{
                                    width: 90, opacity: sel && !added ? 1 : 0.35, padding: '4px 8px', fontSize: 13,
                                    color: sel ? (selItem?.is_returnable === 1 ? '#388e3c' : '#c62828') : '#888',
                                    fontWeight: 600,
                                  }}
                                >
                                  <option value={1}>Yes</option>
                                  <option value={0}>No</option>
                                </Input>
                              </td>
                              <td onClick={(e) => e.stopPropagation()}>
                                <Input
                                  type="textarea"
                                  rows={2}
                                  disabled={!sel || added}
                                  placeholder={sel && !added ? 'Add remarks...' : '—'}
                                  value={selItem?.remarks || ''}
                                  onChange={(e) => updateField(item.sku, 'remarks', e.target.value)}
                                  style={{ opacity: sel && !added ? 1 : 0.35, fontSize: 13, padding: '4px 10px', resize: 'vertical', minHeight: 56 }}
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Complaint No + Driver Name */}
                {soItems.length > 0 && (
                  <div style={{ padding: '16px 24px', borderTop: '1px solid #e3e6ed', background: '#fafbfc', display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 200 }}>
                      <Label style={{ fontWeight: 600, fontSize: 13, color: '#495057', marginBottom: 6 }}>
                        <i className="bx bx-file me-1"></i> Complaint No <span style={{ color: '#dc3545' }}>*</span>
                      </Label>
                      <Input
                        type="text"
                        placeholder="Enter complaint no..."
                        value={complaintNo}
                        onChange={(e) => setComplaintNo(e.target.value)}
                        style={{ fontSize: 13 }}
                        required
                      />
                    </div>
                    <div style={{ flex: 1, minWidth: 200 }}>
                      <Label style={{ fontWeight: 600, fontSize: 13, color: '#495057', marginBottom: 6 }}>
                        <i className="bx bx-car me-1"></i> Driver Name
                      </Label>
                      <Input
                        type="text"
                        placeholder="Enter driver name..."
                        value={driverName}
                        onChange={(e) => setDriverName(e.target.value)}
                        style={{ fontSize: 13 }}
                      />
                    </div>
                  </div>
                )}

                {/* Footer */}
                {soItems.length > 0 && (
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '16px 24px', borderTop: '1px solid #e3e6ed', background: '#f5f6fa',
                  }}>
                    <div>
                      {selectedItems.length > 0 ? (
                        <span className="badge bg-soft-success text-success" style={{ fontSize: 13, padding: '6px 14px' }}>
                          <i className="bx bx-check me-1"></i>
                          {selectedItems.length} item{selectedItems.length > 1 ? 's' : ''} selected
                        </span>
                      ) : (
                        <span style={{ fontSize: 13, color: '#adb5bd' }}>Select items to continue</span>
                      )}
                    </div>
                    <div className="d-flex gap-2">
                      <Button color="light" onClick={handleCancel} disabled={isLoading}>
                        Cancel
                      </Button>
                      <Button
                        color="primary"
                        onClick={handleSubmit}
                        disabled={isSubmitting || selectedItems.length === 0}
                        style={{ fontWeight: 600, minWidth: 140 }}
                      >
                        {isSubmitting
                          ? <><Spinner size="sm" className="me-2" />Submitting...</>
                          : <><i className="bx bx-check-circle me-1"></i>Submit DO</>
                        }
                      </Button>
                    </div>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}
    </>
  );
};

export default ComplaintsDoForm;
